import { useContext, useMemo, useState, useCallback, MouseEvent } from 'react'
import classNames from 'classnames/bind'
import moment from 'moment-timezone'
import pipe from 'ramda/es/pipe'
import map from 'ramda/es/map'
import values from 'ramda/es/values'
import sort from 'ramda/es/sort'
import prop from 'ramda/es/prop'
import descend from 'ramda/es/descend'
import isEmpty from 'ramda/es/isEmpty'
import { useTranslation } from 'react-i18next'

import { Table, Tr, TdLabel, Th, TrSelectable } from 'app/common/ui-components/table'
import toFixedValue from 'app/common/lib/toFixedValue'
import EmptyWarn from 'app/common/layouts/EmptyWarn'
import { useCurrencySign } from 'app/common/components-next/currency'
import { PlainSideSlider } from 'app/common/ui-next/plain-side-slider'
import Sticky from 'app/common/templates-next/sticky'
import { Loading, PageTemplateContent } from 'app/common/templates-next/page-template'

import { SubsidyOrdersContext, type SubsidyOrder } from '../../CafeteriaSubsidyOrdersData'
import ItemDetails, { dateFormat } from './ItemDetails'
import Status from './Status'

import styles from './Subsidies.module.scss';


const cx = classNames.bind(styles)

type SubsidyOrderView = {
  id: number,
  status: 'active' | 'deleted' | 'inactive',
  number: string,
  validPeriod: string,
  useFor: Array<string>,
  type: string | null,
  day: string,
  total: string,
}

type Props = {
  personId: number,
  timezone: string,
  schoolId?: number,
}

const Subsidies = ({ personId, timezone, schoolId }: Props) => {
  const { t } = useTranslation('cafeteria')
  const currency = useCurrencySign('commaSpaceBefore')

  const {
    content: { subsidyOrders },
    loading: { subsidyOrders: loadingSubsidyOrders },
  } = useContext(SubsidyOrdersContext)

  const tableHeaders = t('settings_subsidies_table_columns', { returnObjects: true })

  const subsidyOrdersArray: Array<SubsidyOrderView> = useMemo(() => pipe(
    values,
    map(({
      id,
      number,
      status,
      valid_from: vFrom,
      valid_to: vTo,
      for: useFor,
      type,
      per_day: perDay,
      complexes = [],
      total,
    }: SubsidyOrder) => ({
      id,
      status,
      number,
      validPeriod: `${moment(vFrom).format('YYYY.MM.DD')} — ${moment(vTo).format('YYYY.MM.DD')}`,
      useFor,
      type,
      day: perDay ? String(perDay) : complexes.join(', '),
      total: toFixedValue(total),
      sortableDate: Date.parse(vTo),
    })),
    sort(descend(prop('sortableDate'))),
  )(subsidyOrders), [subsidyOrders])


  // const [refFrom, refTo] = useCopyColumnsWidths()


  const loading = loadingSubsidyOrders
  const nothing = isEmpty(subsidyOrdersArray)


  const [details, setDetails] = useState('')

  const handleChangeDetails = useCallback((id: string, checked: boolean) => {
    if (details === id) {
      setDetails(checked ? id : '')
      return
    }
    setDetails(id)
  }, [details])

  const handleCloseDetails = useCallback((e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setDetails('')
  }, [])

  const selectedSubsidyOrderId = parseInt(details, 10)
  const seletedSubsidyOrder: SubsidyOrder = subsidyOrders[details]
  const lastUsage = (seletedSubsidyOrder && seletedSubsidyOrder.last_usage) || moment().format(dateFormat)
  const subsidyOrderNumber = seletedSubsidyOrder && seletedSubsidyOrder.number

  return (
    <>
      {loading && <PageTemplateContent skipPadding>
        <Loading />
      </PageTemplateContent>}

      {!loading && nothing && <PageTemplateContent skipPadding>
        <EmptyWarn>
          {t('settings_subsidies_table_empty', 'Справки на дотации отсутствуют')}
        </EmptyWarn>
      </PageTemplateContent>}

      {!loading && !nothing
        && <PageTemplateContent className={styles.tableContent} skipPadding>
          <Table className={styles.table}>
            <Sticky applyToChildrenTh>
              {(ref, stuck, shadow) => {
                const thProps = {
                  borderShadowBottom: shadow,
                  borderTopCompensate: stuck,
                  bgFilled: stuck,
                  alignLeft: true,
                }

                return (
                  <thead>
                    <Tr header reference={ref}>
                      <Th {...thProps}>
                        {tableHeaders[0]}
                      </Th>
                      <Th {...thProps}>
                        {tableHeaders[1]}
                      </Th>
                      <Th {...thProps}>
                        {tableHeaders[2]}
                      </Th>
                      <Th {...thProps}>
                        {tableHeaders[3]}
                      </Th>
                      <Th {...thProps}>
                        {tableHeaders[4]}
                      </Th>
                      <Th {...thProps}>
                        {tableHeaders[5]}
                        {currency}
                      </Th>
                      <Th {...thProps}>
                        {tableHeaders[6]}
                        {currency}
                      </Th>
                    </Tr>
                  </thead>
                )
              }}
            </Sticky>

            <tbody>
              {subsidyOrdersArray.map(({ id, status, number, validPeriod, useFor, type, day, total }) => {
                const htmlFor = String(id)
                const ID = String(id)
                const rowClassName = cx({
                  refActive: status === 'active',
                  refInactive: status === 'inactive',
                  refDeleted: status === 'deleted',
                })

                return (
                  <TrSelectable
                    key={ID}
                    id={htmlFor}
                    onClick={handleChangeDetails}
                    selected={details ? ID === details : false}
                    loading={loading}
                    className={rowClassName}
                  >
                    <TdLabel htmlFor={htmlFor} headers="header1"><Status status={status} /></TdLabel>
                    <TdLabel htmlFor={htmlFor} headers="header2">{number}</TdLabel>
                    <TdLabel htmlFor={htmlFor} headers="header3">{validPeriod}</TdLabel>
                    <TdLabel htmlFor={htmlFor} headers="header4">{useFor.map(s => <div key={s}>{s}</div>)}</TdLabel>
                    <TdLabel htmlFor={htmlFor} headers="header5">{type}</TdLabel>
                    <TdLabel htmlFor={htmlFor} headers="header6">{day}</TdLabel>
                    <TdLabel htmlFor={htmlFor} headers="header7">{total}</TdLabel>
                  </TrSelectable>
                )
              })}
            </tbody>
          </Table>
        </PageTemplateContent>}

      <PlainSideSlider
        open={!!details}
        onCrossClick={handleCloseDetails}
        position={{
          top: 0,
          right: 0,
          bottom: 0,
        }}
        classNameOpened={styles.sliderOpened}
      >
        <ItemDetails
          subsidyOrderId={selectedSubsidyOrderId}
          subsidyOrderNumber={subsidyOrderNumber}
          lastUsage={lastUsage}
          personId={personId}
          timezone={timezone}
          schoolId={schoolId}
        />
      </PlainSideSlider>
    </>
  )
}

export default Subsidies
